import React, { useEffect } from 'react'
import { useTheme } from '@mui/material/styles'
import { Box, Link } from '@mui/material'
import MasterCard from '../assets/images/mastercard.svg'
import VisaCard from '../assets/images/visa.svg'
import AmericanExpress from '../assets/images/american-express.svg'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import AddOrUpdateCardDlg from './AddOrUpdateCardDlg'

import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { useDispatch, useSelector } from 'react-redux'
import { useGetMethodMutation, useSetupIntentMutation } from '../redux/api/paymentApi'
import { openCardDlg, closeCardDlg, setCardErrorMessage } from '../redux/features/cardDlgSlice'
import { clearSetupIntent } from '../redux/features/intentSlice'

const capitalizeFirst = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

const stripePromise = process.env.REACT_APP_STRIPE_PK ? loadStripe(`${process.env.REACT_APP_STRIPE_PK}`) : null

function getCardIcon (brand: string | null) {
  switch (brand) {
    case 'mastercard':
      return (
        <Box
          component='img'
          src={MasterCard}
          sx={{ width: '36px', height: '19px', display: 'flex', flexDirection: 'row', border: '1px solid #E8E8E8', borderRadius: '5px', p: '2px 6px' }}
        />
      )
    case 'visa':
      return (
        <Box
          component='img'
          src={VisaCard}
          sx={{ width: '38px', height: '21px', display: 'flex', flexDirection: 'row', border: '1px solid #E8E8E8', borderRadius: '5px', p: '2px 6px' }}
        />
      )
    case 'American Express':
      return (
        <Box
          component='img'
          src={AmericanExpress}
          sx={{ width: '40px', height: '22px', display: 'flex', flexDirection: 'row', border: '1px solid #E8E8E8', borderRadius: '5px', p: '2px 6px' }}
        />
      )
    default:
      return <Box component={CreditCardIcon}/>
  }
}

const UserPayment: React.FC = () => {
  const theme = useTheme()
  const dlg = useSelector((state: any) => state.cardDlgState)
  const [getMethod, getMethodResult] = useGetMethodMutation()
  useEffect(() => {
    getMethod({})
      .then(() => {})
      .catch(() => {})
  }, [dlg.openCardDialog])
  const paymentState = useSelector((state: any) => state.paymentState)
  const { method } = paymentState

  const intentState = useSelector((state: any) => state.intentState)
  const { intent } = intentState
  const clientSecret = intent?.clientSecret
  const [setupIntent] = useSetupIntentMutation()

  useEffect(() => {
    if (!clientSecret && getMethodResult.isSuccess) {
      const cookie = document.cookie.match(/XSRF-TOKEN=([^%]+)/si)
      const xsrf: string = cookie !== null ? cookie[1].concat('=') : 'err'
      setupIntent({ token: xsrf })
        .then(() => {})
        .catch(() => {})
    }
  }, [getMethodResult.isLoading, clientSecret])

  const dispatch = useDispatch()
  const handleOpenCardDialog = () => {
    dispatch(setCardErrorMessage(''))
    dispatch(openCardDlg())
  }
  const handleCloseCardDialog = () => {
    dispatch(closeCardDlg())
    dispatch(clearSetupIntent())
  }
  return (
    <Box>
      <Box sx={{
        alignItems: 'center',
        border: '1px solid #E8E8E8',
        borderRadius: '5px',
        boxShadow: '0px 1px 4px 0px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        m: '20px 0 0',
        p: '10px 11px'
      }}>
      {(method?.paymentMethodId && method?.cardBrand)
        ? (
          <>
          <Box
            sx={{
              flexGrow: 1,
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'row',
              pr: 1
            }}
          >
            {getCardIcon(method?.cardBrand)}
              {(method?.cardLastNumbers) && (
                <Box sx={{ color: theme.palette.grey[45], fontSize: 12, fontWeight: 400, m: '0 15px 0 10px' }}>{capitalizeFirst(method.cardBrand)} ending in {method.cardLastNumbers}</Box>
              )}
              {(method?.cardExpMonth && method?.cardExpYear) && (
                <Box sx={{ color: theme.palette.grey[85], fontSize: 12, fontWeight: 400 }}>{method.cardExpMonth}/{method.cardExpYear - 2000}</Box>
              )}
            </Box>
            {stripePromise && clientSecret && getMethodResult.isSuccess && (
              <>
              <Link sx={{ color: '#919EB6', fontSize: 12, fontWeight: 400 }} onClick={handleOpenCardDialog}>Update</Link>
              <Elements options={{ clientSecret }} stripe={stripePromise}>
                <AddOrUpdateCardDlg handleClose={handleCloseCardDialog} setupIntentSecret={clientSecret} isAddCardDlg={false}/>
              </Elements>
              </>
            )}
          </>
          )
        : (
          <Box
            sx={{
              display: 'flex',
              flexGrow: 1,
              justifyContent: 'center',
              flexDirection: 'row'
            }}
          >
          {(getMethodResult.isSuccess)
            ? (
              <>
                {stripePromise && clientSecret && (
                <>
                <Link sx={{ color: '#919EB6', fontSize: 12, fontWeight: 400 }} onClick={handleOpenCardDialog}>Add Payment Method</Link>
                <Elements options={{ clientSecret }} stripe={stripePromise}>
                  <AddOrUpdateCardDlg handleClose={handleCloseCardDialog} setupIntentSecret={clientSecret} isAddCardDlg={true}/>
                </Elements>
                </>
                )}
              </>
              )
            : (
              <>
                <Box sx={{ color: '#919EB6', fontSize: 12, fontWeight: 400 }}>
                  Adding a payment method is not available
                </Box>
              </>
              )}
          </Box>
          )}
        </Box>
    </Box>
  )
}

export default UserPayment
